'use client';

import { useState, useEffect, useCallback } from 'react';
import { SHA1 } from 'crypto-js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, Upload, X, Sparkles, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { sha1HashContent } from '../../data/sha1-hash-content';
import Link from 'next/link';

const sampleText = 'Hello, world! This is a sample text for SHA-1 hashing.';
const sampleHash = '0a0a9f2a6772942557ab5355d76af442f8f65e01';

export default function SHA1HashTool() {
    const [input, setInput] = useState('');
    const [hash, setHash] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const { toast } = useToast();

    // Generate SHA-1 hash when input changes
    const generateHash = useCallback((text: string) => {
        if (!text.trim()) {
            setHash('');
            return;
        }

        try {
            setIsLoading(true);
            const hash = SHA1(text).toString();
            setHash(hash);
        } catch (error) {
            console.error('Error generating SHA-1 hash:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate SHA-1 hash',
                variant: 'destructive',
            });
            setHash('');
        } finally {
            setIsLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        generateHash(input);
    }, [input, generateHash]);

    const handleCopy = () => {
        if (!hash) return;

        navigator.clipboard.writeText(hash)
            .then(() => {
                setIsCopied(true);
                toast({
                    title: 'Success',
                    description: 'Hash copied to clipboard!',
                });
                setTimeout(() => setIsCopied(false), 2000);
            })
            .catch(() => {
                toast({
                    title: 'Error',
                    description: 'Failed to copy to clipboard',
                    variant: 'destructive',
                });
            });
    };

    const handleClear = () => {
        setInput('');
        setFileName(null);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.onerror = () => {
            toast({
                title: 'Error',
                description: 'Failed to read file',
                variant: 'destructive',
            });
        };
        reader.readAsText(file);
    };

    const handleUseSample = () => {
        setInput(sampleText);
        setFileName(null);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <Toaster />
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <ToolPageIntro title="SHA-1 Hash Generator">
                Generate SHA-1 hashes from text or files. Use for checksums and legacy compatibility. Paste or upload; results update as you type.
            </ToolPageIntro>

                {/* Main content */}
                <div className="grid gap-6 md:grid-cols-2">
                    {/* Input Card */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle>Input</CardTitle>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleUseSample}
                                        disabled={isLoading}
                                    >
                                        <Sparkles className="h-4 w-4 mr-2" />
                                        Sample
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={!input || isLoading}
                                    >
                                        <X className="h-4 w-4 mr-2" />
                                        Clear
                                    </Button>
                                </div>
                            </div>
                            <CardDescription>
                                {fileName ? (
                                    <div className="flex items-center text-sm text-muted-foreground">
                                        <FileText className="h-4 w-4 mr-2" />
                                        {fileName}
                                    </div>
                                ) : (
                                    'Enter text or upload a file to generate SHA-1 hash'
                                )}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="relative">
                                    <Textarea
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        placeholder="Enter text to hash..."
                                        className="min-h-[200px] font-mono text-sm"
                                        disabled={isLoading}
                                    />
                                    <div className="absolute right-2 bottom-2">
                                        <input
                                            type="file"
                                            onChange={handleFileUpload}
                                            className="hidden"
                                            id="file-upload"
                                            disabled={isLoading}
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            asChild
                                            className="h-8"
                                            disabled={isLoading}
                                        >
                                            <label htmlFor="file-upload" className="cursor-pointer">
                                                <Upload className="h-4 w-4 mr-2" />
                                                {fileName ? 'Change File' : 'Upload File'}
                                            </label>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Output Card */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle>SHA-1 Hash</CardTitle>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={!hash || isLoading}
                                >
                                    {isCopied ? (
                                        <Check className="h-4 w-4 mr-2" />
                                    ) : (
                                        <Copy className="h-4 w-4 mr-2" />
                                    )}
                                    {isCopied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                            <CardDescription>
                                {hash ? 'Your SHA-1 hash is ready' : 'Hash will appear here'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className={cn(
                                'relative rounded-md border p-4 font-mono text-sm overflow-x-auto',
                                'min-h-[200px] flex items-center justify-center',
                                hash ? 'bg-muted/50' : 'bg-muted/20',
                                isLoading && 'opacity-70'
                            )}>
                                {isLoading ? (
                                    <div className="flex flex-col items-center justify-center space-y-2 text-muted-foreground">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                        <p>Generating hash...</p>
                                    </div>
                                ) : hash ? (
                                    <pre className="whitespace-pre-wrap break-all">{hash}</pre>
                                ) : (
                                    <p className="text-muted-foreground text-center">
                                        {input ? 'Processing...' : 'Enter text or upload a file to generate SHA-1 hash'}
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Information Section */}
                <Card className="mt-6">
                    <CardHeader>
                        <CardTitle>About SHA-1 Hash</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                            <p>
                                The SHA-1 (Secure Hash Algorithm 1) is a cryptographic hash function that produces a 160-bit (20-byte) hash value.
                                It was designed by the United States National Security Agency and is a U.S. Federal Information Processing Standard.
                            </p>
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 my-4 rounded-r">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.517 2.625H3.732c-1.347 0-2.19-1.458-1.517-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                            <strong>Security Note:</strong> SHA-1 is no longer considered secure against well-funded attackers.
                                            It should not be used for security-sensitive applications. For secure applications, consider using SHA-256 or SHA-3 instead.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-medium mb-2">Common Uses</h3>
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Git version control system
                                </li>
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Checksums for file integrity
                                </li>
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Digital signatures (legacy)
                                </li>
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Certificate fingerprints
                                </li>
                            </ul>
                        </div>

                        <div className="mt-4 p-4 bg-muted/50 rounded-md">
                            <h4 className="text-sm font-medium mb-2">Example</h4>
                            <div className="space-y-2">
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">Input:</p>
                                    <pre className="bg-background p-2 rounded text-xs overflow-x-auto">{sampleText}</pre>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">SHA-1 Hash:</p>
                                    <pre className="bg-background p-2 rounded text-xs overflow-x-auto">{sampleHash}</pre>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={sha1HashContent.title}
                    description={sha1HashContent.description}
                    content={sha1HashContent.content}
                    faqs={sha1HashContent.faqs}
                    relatedTools={sha1HashContent.relatedTools}
                />
            </div>
        </div>
    );
}
